import React, { useState, useRef, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Send, Bot, User, FileText, Loader2, Sparkles, MessageSquare, Brain } from "lucide-react";
import { Button } from "@/components/ui/button";
import SaveToKnowledgeButton from "@/components/kb/SaveToKnowledgeButton";
import axios from "axios";

const API = process.env.REACT_APP_BACKEND_URL;

export default function ChatTab() {
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const [sessionId, setSessionId] = useState(null);
  const scrollRef = useRef(null);

  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [messages]);

  const sendMessage = async () => {
    const text = input.trim();
    if (!text || loading) return;

    const userMsg = { role: "user", content: text };
    setMessages((prev) => [...prev, userMsg]);
    setInput("");
    setLoading(true);

    try {
      const history = messages.map((m) => ({ role: m.role, content: m.content }));
      const res = await axios.post(`${API}/api/knowledge/chat`, {
        message: text,
        history,
        session_id: sessionId,
      });
      const data = res.data;
      if (data.session_id) setSessionId(data.session_id);
      setMessages((prev) => [
        ...prev,
        {
          role: "assistant",
          content: data.answer,
          sources: data.sources || [],
          latency_ms: data.latency_ms,
        },
      ]);
    } catch {
      setMessages((prev) => [
        ...prev,
        { role: "assistant", content: "Something went wrong. Please try again." },
      ]);
    } finally {
      setLoading(false);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  if (messages.length === 0 && !loading) {
    return (
      <div className="flex flex-col items-center justify-center py-16 text-center" data-testid="chat-empty-state" data-automation-id="chat-empty-state">
        <div className="w-14 h-14 rounded-2xl bg-[var(--qn-primary)]/10 flex items-center justify-center mb-5">
          <MessageSquare className="w-7 h-7 text-[var(--qn-primary)]" />
        </div>
        <h3 className="text-lg font-semibold text-[var(--qn-foreground)] mb-2">Chat with your documents</h3>
        <p className="text-sm text-[var(--qn-foreground-muted)] max-w-sm mb-6">
          Ask questions about your uploaded files. The AI will search your knowledge base and answer with source citations.
        </p>
        <div className="w-full max-w-lg">
          <div className="relative">
            <input
              data-testid="chat-input" data-automation-id="chat-input"
              type="text"
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={handleKeyDown}
              placeholder="Ask a question about your documents..."
              className="w-full bg-[var(--qn-surface)] border border-[var(--qn-border)] rounded-xl px-4 py-3 pr-12 text-sm text-[var(--qn-foreground)] placeholder:text-[var(--qn-foreground-subtle)] focus:outline-none focus:border-[var(--qn-primary)] transition-colors"
            />
            <Button
              data-testid="chat-send-btn" data-automation-id="chat-send-btn"
              size="icon"
              onClick={sendMessage}
              disabled={!input.trim()}
              className="absolute right-1.5 top-1.5 h-8 w-8 rounded-lg bg-[var(--qn-primary)] hover:bg-[var(--qn-primary-hover)] disabled:opacity-30"
            >
              <Send className="w-4 h-4" />
            </Button>
          </div>
          <div className="flex flex-wrap gap-2 mt-4 justify-center">
            {["Summarize my documents", "What topics are covered?", "Find key insights"].map((q) => (
              <button
                key={q}
                onClick={() => { setInput(q); }}
                className="text-xs px-3 py-1.5 rounded-full border border-[var(--qn-border)] text-[var(--qn-foreground-muted)] hover:border-[var(--qn-primary)] hover:text-[var(--qn-primary)] transition-colors"
              >
                {q}
              </button>
            ))}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col h-[500px]" data-testid="chat-conversation" data-automation-id="chat-conversation">
      {/* Messages */}
      <div ref={scrollRef} className="flex-1 overflow-y-auto space-y-4 pr-2 mb-4">
        <AnimatePresence>
          {messages.map((msg, i) => (
            <motion.div
              key={i}
              initial={{ opacity: 0, y: 8 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.2 }}
              className={`flex gap-3 ${msg.role === "user" ? "justify-end" : ""}`}
            >
              {msg.role === "assistant" && (
                <div className="w-7 h-7 rounded-lg bg-[var(--qn-primary)]/15 flex items-center justify-center shrink-0 mt-0.5">
                  <Sparkles className="w-4 h-4 text-[var(--qn-primary)]" />
                </div>
              )}
              <div className={`max-w-[80%] ${msg.role === "user"
                  ? "bg-[var(--qn-primary)] text-white rounded-2xl rounded-br-md px-4 py-2.5"
                  : "bg-[var(--qn-surface)] border border-[var(--qn-border)] rounded-2xl rounded-bl-md px-4 py-3"
                }`}
              >
                <div className="text-sm leading-relaxed whitespace-pre-wrap" data-testid={`chat-msg-${i}`}>
                  {msg.content}
                </div>
                {msg.sources && msg.sources.length > 0 && (
                  <div className="mt-3 pt-2.5 border-t border-[var(--qn-border)]/50">
                    <p className="text-[10px] uppercase tracking-wider text-[var(--qn-foreground-subtle)] mb-1.5">Sources</p>
                    <div className="flex flex-wrap gap-1.5">
                      {msg.sources.map((s, j) => (
                        <span
                          key={j}
                          className={`inline-flex items-center gap-1 text-[11px] px-2 py-0.5 rounded-md ${s.kind === "knowledge_base" ? "bg-emerald-500/10 text-emerald-600" : "bg-[var(--qn-primary)]/10 text-[var(--qn-primary)]"}`}
                          data-testid={`chat-source-${j}`}
                        >
                          {s.kind === "knowledge_base" ? <Brain className="w-3 h-3" /> : <FileText className="w-3 h-3" />}
                          {s.document_title}
                        </span>
                      ))}
                    </div>
                  </div>
                )}
                {msg.latency_ms != null && (
                  <p className="text-[10px] text-[var(--qn-foreground-subtle)] mt-1.5">{msg.latency_ms}ms</p>
                )}
                {msg.role === "assistant" && msg.content && (
                  <div className="mt-2">
                    <SaveToKnowledgeButton
                      content={msg.content}
                      defaultTitle={(messages[i - 1]?.content || msg.content).slice(0, 60)}
                      sourceType="conversation"
                      contentType="insight"
                      label="Save"
                      variant="ghost"
                      size="sm"
                      className="h-6 text-[11px] px-1.5 text-[var(--qn-foreground-subtle)]"
                    />
                  </div>
                )}
              </div>
              {msg.role === "user" && (
                <div className="w-7 h-7 rounded-lg bg-[var(--qn-foreground)]/10 flex items-center justify-center shrink-0 mt-0.5">
                  <User className="w-4 h-4 text-[var(--qn-foreground)]" />
                </div>
              )}
            </motion.div>
          ))}
        </AnimatePresence>
        {loading && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="flex gap-3">
            <div className="w-7 h-7 rounded-lg bg-[var(--qn-primary)]/15 flex items-center justify-center shrink-0">
              <Sparkles className="w-4 h-4 text-[var(--qn-primary)]" />
            </div>
            <div className="bg-[var(--qn-surface)] border border-[var(--qn-border)] rounded-2xl rounded-bl-md px-4 py-3">
              <Loader2 className="w-4 h-4 animate-spin text-[var(--qn-primary)]" />
            </div>
          </motion.div>
        )}
      </div>

      {/* Input */}
      <div className="relative">
        <input
          data-testid="chat-input" data-automation-id="chat-input"
          type="text"
          value={input}
          onChange={(e) => setInput(e.target.value)}
          onKeyDown={handleKeyDown}
          placeholder="Ask a follow-up question..."
          className="w-full bg-[var(--qn-surface)] border border-[var(--qn-border)] rounded-xl px-4 py-3 pr-12 text-sm text-[var(--qn-foreground)] placeholder:text-[var(--qn-foreground-subtle)] focus:outline-none focus:border-[var(--qn-primary)] transition-colors"
        />
        <Button
          data-testid="chat-send-btn" data-automation-id="chat-send-btn"
          size="icon"
          onClick={sendMessage}
          disabled={!input.trim() || loading}
          className="absolute right-1.5 top-1.5 h-8 w-8 rounded-lg bg-[var(--qn-primary)] hover:bg-[var(--qn-primary-hover)] disabled:opacity-30"
        >
          {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
        </Button>
      </div>
    </div>
  );
}
